import React, { useState } from "react";
import {
    ArrowRight,
    Play,
    CheckCircle2,
    Sparkles,
    Clock,
    TrendingUp,
    Zap,
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { stats } from "../mock";
import heroImg from "../assets/hero_img.png";

const Hero = ({ onBookDemo }) => {
    const [isHovered, setIsHovered] = useState(false);
    const handleWatchDemo = () => {
        const section = document.getElementById("demo-video");
        section?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-slate-50 to-white">
            {/* Background decoration */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-20 left-10 w-72 h-72 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
                <div className="absolute top-40 right-10 w-72 h-72 bg-teal-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
                <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-slate-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
            </div>

            <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-28">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Left content */}
                    <div className="text-left space-y-8">
                        {/* Badge */}
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full">
                            <Sparkles className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-blue-700">
                                AI Chief of Staff for Engineering
                            </span>
                        </div>

                        {/* Headline */}
                        <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                            Stop Explaining Past.
                            <span className="relative inline-block">
                                <span className="relative z-10 bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                                    Start Building Future.
                                </span>
                                <div className="absolute bottom-2 left-0 right-0 h-3 bg-blue-200 opacity-30 -rotate-1"></div>
                            </span>{" "}
                            Your Vision Awaits.
                        </h1>

                        {/* Subheadline */}
                        <p className="text-xl text-slate-600 leading-relaxed max-w-xl">
                            Nucleus connects to your engineering tools and uses
                            AI to answer the questions that waste your time:
                            What shipped? Why are we behind? Who should build
                            this?
                        </p>

                        {/* Value props */}
                        <div className="space-y-3">
                            {[
                                "AI-generated release notes in 5 minutes",
                                "Instant sprint intelligence & risk analysis",
                                "Context-aware developer recommendations",
                            ].map((item, idx) => (
                                <div
                                    key={idx}
                                    className="flex items-center gap-3 group"
                                >
                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center group-hover:bg-teal-200 transition-colors">
                                        <CheckCircle2 className="w-4 h-4 text-teal-700" />
                                    </div>
                                    <span className="text-slate-700 font-medium">
                                        {item}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* CTAs */}
                        <div className="flex flex-wrap gap-4 pt-4">
                            <Button
                                size="lg"
                                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                                onClick={onBookDemo}
                            >
                                Book a Demo
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                            <Button
                                size="lg"
                                variant="outline"
                                className="border-2 border-slate-300 hover:border-slate-400 px-8 py-6 text-lg font-semibold rounded-lg hover:bg-slate-50 transition-all duration-300"
                                onClick={handleWatchDemo}
                                onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}
                            >
                                <Play
                                    className={`mr-2 w-5 h-5 transition-transform duration-300 ${isHovered ? "scale-110" : ""}`}
                                />
                                Watch Demo
                            </Button>
                        </div>

                        {/* Trust indicator */}
                        <p className="text-sm text-slate-500 flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            10-minute setup • No credit card required
                        </p>
                    </div>

                    {/* Right content - Hero image */}
                    <div className="relative">
                        <div
                            className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200 hover:shadow-3xl transition-shadow duration-500"
                            style={{ backgroundColor: "#2b2235" }}
                        >
                            <img
                                src={heroImg}
                                alt="AI Technology Dashboard"
                                className="w-full h-auto object-cover bg-transparent"
                            />
                        </div>

                        {/* Decorative elements */}
                        <div className="absolute -top-6 -right-6 w-24 h-24 bg-blue-200 rounded-full opacity-20 blur-2xl"></div>
                        <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-teal-200 rounded-full opacity-20 blur-2xl"></div>
                    </div>
                </div>

                {/* Stats bar */}
                <div className="mt-20 pt-12 border-t border-slate-200">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {stats.map((stat, idx) => (
                            <div key={idx} className="text-center">
                                <p className="text-4xl font-bold text-slate-900 mb-2">
                                    {stat.value}
                                    {stat.suffix}
                                </p>
                                <p className="text-sm text-slate-600">
                                    {stat.label}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
